import OwnerCertificationChartTooltip from 'components/tooltip/OwnerCertificationChartTooltip';
import OwnerCertificationChartTooltipCursor from 'components/tooltip/OwnerCertificationChartTooltipCursor';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartOfferForm, ChartOption } from 'types/chart';
import { formatDanjiAveragePriceTick } from 'utils/formatUtil';

interface Props {
  chartData: ChartOfferForm[];
  range: number;
  chartOption: ChartOption;
  onChartClick: () => void;
  onChartMouseLeave: () => void;
  chartKey: string;
}

function OwnerCertificationChart({
  chartData,
  chartOption,
  chartKey,
  range,
  onChartClick,
  onChartMouseLeave,
}: Props) {
  const { tooltipActive, xAxisProps, yAxisProps } = chartOption;

  const CustomBarShape = (props) => {
    const {
      fill,
      x,
      y,
      width,
      height,
      verificationCount,
      unverifiedOfferCount,
      dataKey,
    } = props;
    const radius = 2; // Adjust the radius as needed

    if (dataKey === 'unverifiedOfferCount' && !unverifiedOfferCount)
      return null;
    if (dataKey === 'verificationCount' && !verificationCount) return null;
    return (
      <path
        d={`
      M${x},${y}
      h${width - radius}
      a${radius},${radius} 0 0 1 ${radius},${radius}
      v${height - 2 * radius}
      a${radius},${radius} 0 0 1 -${radius},${radius}
      h-${width - radius}
      z
    `}
        fill={fill}
      />
    );
  };

  return (
    <ResponsiveContainer
      width="100%"
      height={160}
      style={{
        overflow: 'hidden',
      }}
    >
      <BarChart
        data={chartData}
        key={chartKey}
        barSize={14}
        layout="vertical"
        onClick={onChartClick}
        onMouseLeave={onChartMouseLeave}
      >
        <XAxis
          hide={xAxisProps?.hide}
          type="number"
          height={20}
          interval={xAxisProps?.interval}
          tick={{
            fill: '#BDBDBD',
            fontSize: '0.6rem',
            fontWeight: 700,
          }}
          tickLine={false}
          axisLine={{
            stroke: '#BDBDBD',
            strokeWidth: 1,
          }}
          padding={{
            right: 10,
          }}
        />
        <YAxis
          dataKey="price"
          type={yAxisProps?.type}
          reversed
          hide={yAxisProps?.hide}
          tickFormatter={(value) => formatDanjiAveragePriceTick(value)}
          width={40}
          tick={{
            fill: '#BDBDBD',
            fontSize: '0.6rem',
            fontWeight: 700,
            dy: 9,
          }}
          tickLine={false}
          axisLine={{
            stroke: '#BDBDBD',
            strokeWidth: 1,
          }}
          padding={{
            bottom: 5,
            top: -15,
          }}
        />

        <Tooltip
          active={tooltipActive}
          content={<OwnerCertificationChartTooltip range={range} />}
          position={{
            x: 150,
          }}
          cursor={<OwnerCertificationChartTooltipCursor />}
        />

        <Bar
          dataKey="verificationCount"
          stackId={1}
          shape={<CustomBarShape />}
          isAnimationActive={false}
        >
          {chartData.map((value) => (
            <Cell
              key={value.price}
              fill={value.isHopePrice ? '#344BFD' : '#BBD2FF'}
            />
          ))}
        </Bar>

        <Bar
          dataKey="unverifiedOfferCount"
          stackId={1}
          shape={<CustomBarShape />}
          isAnimationActive={false}
          fill="#E0EAFF"
        >
          {' '}
          {chartData.map((value) => (
            <Cell
              key={value.price}
              fill={
                value.isHopePrice && value.verificationCount === 0
                  ? '#344BFD'
                  : '#E0EAFF'
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default OwnerCertificationChart;
